import { CalendarIcon, ChevronDownIcon, HelpIcon, TicketIcon } from '@eventbrite/marmalade';
import { defineMessage } from 'react-intl';
import { navigationProps } from '../types';
import { alternativePath } from '../utils';

export const defaultNavigation = ({
    helpCenterLanguage,
    tld,
    showIntentToggle = false,
}: {
    helpCenterLanguage: string;
    tld: string;
    showIntentToggle?: boolean;
}): navigationProps => {
    const navItems = {
        desktop: [
            {
                path: 'd/local/events/',
                translatedText: defineMessage({ id: 'Find Events', defaultMessage: 'Find Events' }),
                heapIdentifier: 'Find Events',
            },
            {
                path: 'organizer/overview/',
                translatedText: defineMessage({
                    id: 'Create Events',
                    defaultMessage: 'Create Events',
                }),
                heapIdentifier: 'Create Events',
            },
            {
                translatedText: defineMessage({ id: 'Help Center', defaultMessage: 'Help Center' }),
                heapIdentifier: 'Help Center',
                iconType: <ChevronDownIcon />,
                iconPosition: 'right',
                dropdownItems: [
                    {
                        path: `help/${helpCenterLanguage}/`,
                        translatedText: defineMessage({
                            id: 'Help Center',
                            defaultMessage: 'Help Center',
                        }),
                        heapIdentifier: 'Help Center',
                    },
                    {
                        path: `help/${helpCenterLanguage}/articles/319355/where-are-my-tickets/`,
                        translatedText: defineMessage({
                            id: 'Find your tickets',
                            defaultMessage: 'Find your tickets',
                        }),
                        heapIdentifier: 'Find your tickets',
                    },
                    {
                        path: `help/${helpCenterLanguage}/articles/647151/how-to-contact-the-event-organizer/`,
                        translatedText: defineMessage({
                            id: 'Contact your event organizer',
                            defaultMessage: 'Contact your event organizer',
                        }),
                        heapIdentifier: 'Contact your event organizer',
                    },
                ],
            },
        ],

        mobile: [
            {
                path: 'd/local/events/',
                translatedText: defineMessage({ id: 'Find Events', defaultMessage: 'Find Events' }),
                heapIdentifier: 'Find Events',
                iconType: <TicketIcon />,
            },
            {
                translatedText: defineMessage({
                    id: 'Create Events',
                    defaultMessage: 'Create Events',
                }),
                heapIdentifier: 'Create Events',
                iconType: <CalendarIcon />,
                dropdownItems: [
                    {
                        translatedText: defineMessage({
                            id: 'Solutions',
                            defaultMessage: 'Solutions',
                        }),
                        heapIdentifier: 'Solutions',
                        dropdownItems: [
                            {
                                translatedText: defineMessage({
                                    id: 'Event Ticketing',
                                    defaultMessage: 'Event Ticketing',
                                }),
                                heapIdentifier: 'Event Ticketing',
                                path: alternativePath('l/sell-tickets/', tld),
                            },
                            {
                                path: 'l/event-marketing-platform/',
                                translatedText: defineMessage({
                                    id: 'Event Marketing Platform',
                                    defaultMessage: 'Event Marketing Platform',
                                }),
                                heapIdentifier: 'Marketing Suite',
                            },
                            {
                                path: 'l/eventbrite-ads/',
                                translatedText: defineMessage({
                                    id: 'Eventbrite Ads',
                                    defaultMessage: 'Eventbrite Ads',
                                }),
                                heapIdentifier: 'Eventbrite Ads',
                            },
                            {
                                path: 'l/event-payment/',
                                translatedText: defineMessage({
                                    id: 'Payments',
                                    defaultMessage: 'Payments',
                                }),
                                heapIdentifier: 'Payments',
                            },
                        ],
                    },
                    {
                        translatedText: defineMessage({
                            id: 'Industry',
                            defaultMessage: 'Industry',
                        }),
                        heapIdentifier: 'Industry',
                        dropdownItems: [
                            {
                                path: 'l/music/',
                                translatedText: defineMessage({
                                    id: 'Music',
                                    defaultMessage: 'Music',
                                }),
                                heapIdentifier: 'Music',
                            },
                            {
                                path: 'l/food-drink-event-ticketing/',
                                translatedText: defineMessage({
                                    id: 'Food & Beverage',
                                    defaultMessage: 'Food & Beverage',
                                }),
                                heapIdentifier: 'Food & Beverage',
                            },
                            {
                                path: 'l/performing-arts/',
                                translatedText: defineMessage({
                                    id: 'Performing Arts',
                                    defaultMessage: 'Performing Arts',
                                }),
                                heapIdentifier: 'Performing Arts',
                            },
                            {
                                path: 'l/npo/',
                                translatedText: defineMessage({
                                    id: 'Charity & Causes',
                                    defaultMessage: 'Charity & Causes',
                                }),
                                heapIdentifier: 'Charity & Causes',
                            },
                            {
                                path: 'l/host-retail-events/',
                                translatedText: defineMessage({
                                    id: 'Retail',
                                    defaultMessage: 'Retail',
                                }),
                                heapIdentifier: 'Retail',
                            },
                        ],
                    },
                    {
                        translatedText: defineMessage({
                            id: 'Event Types',
                            defaultMessage: 'Event Types',
                        }),
                        heapIdentifier: 'Event Types',
                        dropdownItems: [
                            {
                                path: 'l/music-venues/',
                                translatedText: defineMessage({
                                    id: 'Concerts',
                                    defaultMessage: 'Concerts',
                                }),
                                heapIdentifier: 'Concerts',
                            },
                            {
                                path: 'l/create-a-workshop/',
                                translatedText: defineMessage({
                                    id: 'Classes & Workshops',
                                    defaultMessage: 'Classes & Workshops',
                                }),
                                heapIdentifier: 'Classes & Workshops',
                            },
                            {
                                path: 'l/festival-solutions/',
                                translatedText: defineMessage({
                                    id: 'Festivals & Fairs',
                                    defaultMessage: 'Festivals & Fairs',
                                }),
                                heapIdentifier: 'Festivals & Fairs',
                            },
                            {
                                path: alternativePath('l/conferences/', tld),
                                translatedText: defineMessage({
                                    id: 'Conferences',
                                    defaultMessage: 'Conferences',
                                }),
                                heapIdentifier: 'Conferences',
                            },
                            {
                                path: 'l/eventbrite-for-business/',
                                translatedText: defineMessage({
                                    id: 'Corporate Events',
                                    defaultMessage: 'Corporate Events',
                                }),
                                heapIdentifier: 'Corporate Events',
                            },
                            {
                                path: 'l/virtual-events-platform/',
                                translatedText: defineMessage({
                                    id: 'Online Events',
                                    defaultMessage: 'Online Events',
                                }),
                                heapIdentifier: 'Online Events',
                            },
                        ],
                    },
                    {
                        translatedText: defineMessage({ id: 'Blog', defaultMessage: 'Blog' }),
                        heapIdentifier: 'Blog',
                        dropdownItems: [
                            {
                                path: 'blog/category/tips-and-guides/',
                                translatedText: defineMessage({
                                    id: 'Tips & Guides',
                                    defaultMessage: 'Tips & Guides',
                                }),
                                heapIdentifier: 'Tips & Guides',
                            },
                            {
                                path: 'blog/category/news-and-trends/',
                                translatedText: defineMessage({
                                    id: 'News & Trends',
                                    defaultMessage: 'News & Trends',
                                }),
                                heapIdentifier: 'News & Trends',
                            },
                            {
                                path: 'blog/category/community/',
                                translatedText: defineMessage({
                                    id: 'Community',
                                    defaultMessage: 'Community',
                                }),
                                heapIdentifier: 'Community',
                            },
                            {
                                path: 'blog/category/tools-and-features/',
                                translatedText: defineMessage({
                                    id: 'Tools & Features',
                                    defaultMessage: 'Tools & Features',
                                }),
                                heapIdentifier: 'Tools & Features',
                            },
                        ],
                    },
                ],
            },
            {
                path: 'organizer/overview/',
                translatedText: defineMessage({
                    id: 'Create Events',
                    defaultMessage: 'Create Events',
                }),
                heapIdentifier: 'Create Events',
            },
            {
                path: 'l/contact-eventbrite-sales/',
                translatedText: defineMessage({
                    id: 'Contact Sales',
                    defaultMessage: 'Contact Sales',
                }),
                heapIdentifier: 'Contact Sales',
            },
            {
                // TODO: Verify if this link always assumes creator intent
                path: 'signin/signup/?referrer=/manage/events/create/',
                translatedText: defineMessage({ id: 'Get Started', defaultMessage: 'Get Started' }),
                heapIdentifier: 'Get Started',
                textColor: '#3659E3',
            },
            {
                translatedText: defineMessage({ id: 'Help Center', defaultMessage: 'Help Center' }),
                heapIdentifier: 'Help Center',
                iconType: <HelpIcon />,
                dropdownItems: [
                    {
                        path: `help/${helpCenterLanguage}/`,
                        translatedText: defineMessage({
                            id: 'Help Center',
                            defaultMessage: 'Help Center',
                        }),
                        heapIdentifier: 'Help Center',
                    },
                    {
                        path: `help/${helpCenterLanguage}/articles/319355/where-are-my-tickets/`,
                        translatedText: defineMessage({
                            id: 'Find your tickets',
                            defaultMessage: 'Find your tickets',
                        }),
                        heapIdentifier: 'Find your tickets',
                    },
                    {
                        path: `help/${helpCenterLanguage}/articles/647151/how-to-contact-the-event-organizer/`,
                        translatedText: defineMessage({
                            id: 'Contact your event organizer',
                            defaultMessage: 'Contact your event organizer',
                        }),
                        heapIdentifier: 'Contact your event organizer',
                    },
                ],
            },
        ],
    };
    if (tld === '.com') {
        navItems.mobile.splice(2, 0, {
            path: 'resources/',
            translatedText: defineMessage({
                id: 'Organizer Resource Hub',
                defaultMessage: 'Organizer Resource Hub',
            }),
            heapIdentifier: 'Organizer Resource Hub',
        });
    }

    if (showIntentToggle) {
        navItems.mobile = [
            {
                path: '/signin',
                translatedText: defineMessage({ id: 'Log In', defaultMessage: 'Log In' }),
                heapIdentifier: 'login',
            },
            {
                path: '/signin/signup',
                translatedText: defineMessage({ id: 'Sign Up', defaultMessage: 'Sign Up' }),
                heapIdentifier: 'signup',
            },
            {
                path: '/mytickets',
                translatedText: defineMessage({
                    id: 'Find my tickets',
                    defaultMessage: 'Find my tickets',
                }),
                heapIdentifier: 'find-my-tickets',
            },
            ...navItems.mobile,
        ];
    }

    return navItems;
};
